import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { DatePicker } from '../components/ui/DatePicker';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, FileTextIcon, TrendingUpIcon, CreditCardIcon, RefreshCwIcon, CalendarIcon, AlertCircleIcon, BarChart3Icon } from 'lucide-react';
type DatePreset = 'this_month' | 'last_month' | 'last_12_months' | 'this_year' | 'last_year' | 'custom';
// Helper: format date as mm-dd-yyyy for API
const formatDateForAPI = (dateStr: string): string => {
  const d = new Date(dateStr);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};
// Get date range for presets
const getDateRange = (preset: DatePreset): {
  from: string;
  to: string;
} => {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  switch (preset) {
    case 'this_month':
      {
        const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        return {
          from,
          to: today
        };
      }
    case 'last_month':
      {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          from: lastMonth.toISOString().split('T')[0],
          to: lastDay.toISOString().split('T')[0]
        };
      }
    case 'last_12_months':
      {
        const from = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() + 1);
        return {
          from: from.toISOString().split('T')[0],
          to: today
        };
      }
    case 'this_year':
      return {
        from: `${now.getFullYear()}-01-01`,
        to: today
      };
    case 'last_year':
      {
        const ly = now.getFullYear() - 1;
        return {
          from: `${ly}-01-01`,
          to: `${ly}-12-31`
        };
      }
    default:
      return {
        from: '',
        to: ''
      };
  }
};
// Status data for pie chart
interface StatusData {
  name: string;
  value: number;
  count: number;
  color: string;
}
const STATUS_COLORS: Record<string, string> = {
  paid: '#22c55e',
  sent: '#3b82f6',
  overdue: '#ef4444',
  partial_paid: '#f59e0b',
  draft: '#9ca3af',
  voided: '#6b7280'
};
const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  sent: 'Sent',
  overdue: 'Overdue',
  partial_paid: 'Partial',
  draft: 'Draft',
  voided: 'Voided'
};
const ALL_STATUSES = ['paid', 'sent', 'overdue', 'partial_paid', 'draft', 'voided'];
// Custom tooltip for pie chart
const PieTooltip = ({
  active,
  payload
}: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0];
  return <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm" data-id="element-2480">
      <p className="font-semibold text-gray-900" data-id="element-2481">{data.name}</p>
      <p className="text-gray-600" data-id="element-2482">
        {formatCurrency(data.value)} · {data.payload.count} invoice
        {data.payload.count !== 1 ? 's' : ''}
      </p>
    </div>;
};
export function SalesReport() {
  const {
    selectedCompanyId
  } = useData();
  // Filter state
  const [preset, setPreset] = useState<DatePreset>('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Data state
  const [totalSales, setTotalSales] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [unpaidAmount, setUnpaidAmount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [statusData, setStatusData] = useState<StatusData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Get effective date range
  const getEffectiveDateRange = useCallback(() => {
    if (preset === 'custom') return {
      from: customFrom,
      to: customTo
    };
    return getDateRange(preset);
  }, [preset, customFrom, customTo]);
  // Fetch report data using aggregation API
  const fetchReportData = useCallback(async () => {
    if (!selectedCompanyId) return;
    const {
      from,
      to
    } = getEffectiveDateRange();
    if (!from || !to) return;
    setIsLoading(true);
    setError(null);
    try {
      const apiFrom = formatDateForAPI(from);
      const apiTo = formatDateForAPI(to);
      // Fetch all aggregations in parallel: total + per-status
      const [totalAgg, ...statusAggs] = await Promise.all([callBillingAPI('getInvoicesAggregation', {
        company_id: selectedCompanyId,
        from_date: apiFrom,
        to_date: apiTo
      }), ...ALL_STATUSES.map(status => callBillingAPI('getInvoicesAggregation', {
        company_id: selectedCompanyId,
        status,
        from_date: apiFrom,
        to_date: apiTo
      }))]);
      // Total stats
      const total = parseFloat(totalAgg.total_amount) || 0;
      const count = parseInt(totalAgg.total_count) || 0;
      setTotalSales(total);
      setInvoiceCount(count);
      // Per-status breakdown
      const statusResults: StatusData[] = [];
      let paid = 0;
      let unpaid = 0;
      ALL_STATUSES.forEach((status, i) => {
        const agg = statusAggs[i];
        const amount = parseFloat(agg.total_amount) || 0;
        const cnt = parseInt(agg.total_count) || 0;
        if (cnt > 0) {
          statusResults.push({
            name: STATUS_LABELS[status] || status,
            value: amount,
            count: cnt,
            color: STATUS_COLORS[status] || '#9ca3af'
          });
        }
        if (status === 'paid') {
          paid = amount;
        } else if (['sent', 'overdue', 'partial_paid'].includes(status)) {
          unpaid += amount;
        }
      });
      setPaidAmount(paid);
      setUnpaidAmount(unpaid);
      setStatusData(statusResults.sort((a, b) => b.value - a.value));
    } catch (err) {
      console.error('Failed to fetch sales report data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sales data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, getEffectiveDateRange]);
  // Fetch on mount and when company/preset changes
  useEffect(() => {
    fetchReportData();
  }, [selectedCompanyId, preset]);
  // For custom dates, fetch when both are set
  useEffect(() => {
    if (preset === 'custom' && customFrom && customTo) {
      fetchReportData();
    }
  }, [customFrom, customTo]);
  const handlePresetChange = (newPreset: DatePreset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      setCustomFrom('');
      setCustomTo('');
    }
  };
  // Date range display label
  const getDateRangeLabel = () => {
    const {
      from,
      to
    } = getEffectiveDateRange();
    if (!from || !to) return '';
    const fmt = (d: Date) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `${fmt(new Date(from))} – ${fmt(new Date(to))}`;
  };
  const presetButtons: {
    key: DatePreset;
    label: string;
  }[] = [{
    key: 'this_month',
    label: 'This Month'
  }, {
    key: 'last_month',
    label: 'Last Month'
  }, {
    key: 'last_12_months',
    label: 'Last 12 Months'
  }, {
    key: 'this_year',
    label: 'This Year'
  }, {
    key: 'last_year',
    label: 'Last Year'
  }, {
    key: 'custom',
    label: 'Custom'
  }];
  return <div className="space-y-6" data-id="element-2483">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2484">
        <div data-id="element-2485">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2486">Sales Report</h1>
          <p className="text-gray-500 mt-1" data-id="element-2487">
            {getDateRangeLabel() && <span className="flex items-center gap-1.5" data-id="element-2488">
                <CalendarIcon className="w-4 h-4" data-id="element-2489" />
                {getDateRangeLabel()}
                {invoiceCount > 0 && !isLoading && <span className="text-gray-400 ml-1" data-id="element-2490">
                    · {invoiceCount} invoice
                    {invoiceCount !== 1 ? 's' : ''}
                  </span>}
              </span>}
          </p>
        </div>
        <Button variant="secondary" onClick={fetchReportData} disabled={isLoading} leftIcon={<RefreshCwIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-2492" />} data-id="element-2491">
          Refresh
        </Button>
      </div>

      {/* Date Filters */}
      <Card data-id="element-2493">
        <div className="space-y-4" data-id="element-2494">
          <div className="flex flex-wrap gap-2" data-id="element-2495">
            {presetButtons.map(btn => <Button key={btn.key} variant={preset === btn.key ? 'primary' : 'secondary'} size="sm" onClick={() => handlePresetChange(btn.key)} data-id="element-2496">
                {btn.label}
              </Button>)}
          </div>
          {preset === 'custom' && <div className="flex flex-col sm:flex-row gap-4 pt-2 border-t border-gray-200" data-id="element-2497">
              <div className="w-full sm:w-48" data-id="element-2498">
                <DatePicker label="From Date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} data-id="element-2499" />
              </div>
              <div className="w-full sm:w-48" data-id="element-2500">
                <DatePicker label="To Date" value={customTo} onChange={e => setCustomTo(e.target.value)} data-id="element-2501" />
              </div>
            </div>}
        </div>
      </Card>

      {/* Loading */}
      {isLoading && <Card data-id="element-2502">
          <div className="flex items-center gap-3 py-2" data-id="element-2503">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" data-id="element-2504" />
            <p className="text-sm text-gray-600" data-id="element-2505">Loading sales data...</p>
          </div>
        </Card>}

      {/* Error */}
      {error && <Card data-id="element-2506">
          <div className="flex items-start gap-3 p-2" data-id="element-2507">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2508">
              <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-2509" />
            </div>
            <div data-id="element-2510">
              <p className="text-sm font-medium text-gray-900" data-id="element-2511">
                Failed to load sales data
              </p>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-2512">{error}</p>
              <Button size="sm" onClick={fetchReportData} className="mt-2" data-id="element-2513">
                Retry
              </Button>
            </div>
          </div>
        </Card>}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2514">
        <StatCard title="Total Sales" value={isLoading ? '—' : formatCurrency(totalSales)} icon={<DollarSignIcon className="w-6 h-6" data-id="element-2516" />} data-id="element-2515" />
        <StatCard title="Collected (Paid)" value={isLoading ? '—' : formatCurrency(paidAmount)} icon={<CreditCardIcon className="w-6 h-6" data-id="element-2518" />} className="border-green-200 bg-green-50" data-id="element-2517" />
        <StatCard title="Outstanding" value={isLoading ? '—' : formatCurrency(unpaidAmount)} icon={<TrendingUpIcon className="w-6 h-6" data-id="element-2520" />} className={unpaidAmount > 0 ? 'border-amber-200 bg-amber-50' : ''} data-id="element-2519" />
        <StatCard title="Invoices" value={isLoading ? '—' : invoiceCount} icon={<FileTextIcon className="w-6 h-6" data-id="element-2522" />} data-id="element-2521" />
      </div>

      {/* Status Breakdown */}
      {!isLoading && !error && statusData.length > 0 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2523">
          {/* Pie Chart */}
          <div data-id="element-2524">
            <Card data-id="element-2525">
              <CardHeader title="By Status" subtitle="Invoice amount breakdown" data-id="element-2526" />
              <div className="mt-4" style={{
            height: 320
          }} data-id="element-2527">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2528">
                  <PieChart data-id="element-2529">
                    <Pie data={statusData} cx="50%" cy="45%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" data-id="element-2530">
                      {statusData.map((entry, index) => <Cell key={index} fill={entry.color} data-id="element-2531" />)}
                    </Pie>
                    <Tooltip content={<PieTooltip data-id="element-2533" />} data-id="element-2532" />
                    <Legend verticalAlign="bottom" iconType="circle" iconSize={8} formatter={(value: string) => <span className="text-xs text-gray-600" data-id="element-2535">{value}</span>} data-id="element-2534" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Status Summary Table */}
          <div className="lg:col-span-2" data-id="element-2536">
            <Card data-id="element-2537">
              <CardHeader title="Status Breakdown" data-id="element-2538" />
              <div className="mt-4 overflow-x-auto" data-id="element-2539">
                <table className="min-w-full text-sm" data-id="element-2540">
                  <thead data-id="element-2541">
                    <tr className="border-b border-gray-200" data-id="element-2542">
                      <th className="text-left py-2 pr-4 font-semibold text-gray-600" data-id="element-2543">
                        Status
                      </th>
                      <th className="text-right py-2 px-4 font-semibold text-gray-600" data-id="element-2544">
                        Invoices
                      </th>
                      <th className="text-right py-2 pl-4 font-semibold text-gray-600" data-id="element-2545">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2546">
                    {statusData.map(row => <tr key={row.name} className="border-b border-gray-100 last:border-0" data-id="element-2547">
                        <td className="py-2.5 pr-4" data-id="element-2548">
                          <div className="flex items-center gap-2" data-id="element-2549">
                            <div className="w-3 h-3 rounded-full" style={{
                        backgroundColor: row.color
                      }} data-id="element-2550" />
                            <span className="font-medium text-gray-900" data-id="element-2551">
                              {row.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-2.5 px-4 text-right text-gray-600" data-id="element-2552">
                          {row.count}
                        </td>
                        <td className="py-2.5 pl-4 text-right font-medium text-gray-900" data-id="element-2553">
                          {formatCurrency(row.value)}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot data-id="element-2554">
                    <tr className="border-t-2 border-gray-200" data-id="element-2555">
                      <td className="py-2.5 pr-4 font-bold text-gray-900" data-id="element-2556">
                        Total
                      </td>
                      <td className="py-2.5 px-4 text-right font-bold text-gray-900" data-id="element-2557">
                        {invoiceCount}
                      </td>
                      <td className="py-2.5 pl-4 text-right font-bold text-gray-900" data-id="element-2558">
                        {formatCurrency(totalSales)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          </div>
        </div>}

      {/* Empty state */}
      {!isLoading && !error && invoiceCount === 0 && <Card data-id="element-2559">
          <div className="py-12 text-center" data-id="element-2560">
            <BarChart3Icon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2561" />
            <p className="text-gray-500 font-medium" data-id="element-2562">No invoices found</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-2563">
              Try adjusting the date range to see sales data.
            </p>
          </div>
        </Card>}
    </div>;
}